import { CAREER_ACCENTS, type CareerAccent, type CareerSitePreviewInput, type CareerSiteSettings, type CareerSiteSettingsView } from '@nest-recruit/shared';

// ADMIN-12 (Admin redesign 2026-11-02, Option A of 4-messages/career-site.html): what the form stands on, what has
// changed from it, and the draft the live preview draws. Pure, so each rule is tested without a browser.

/** The form's values as `useWatch` gives them: a part may be missing while its field mounts. */
export interface WatchedSettings {
  readonly siteTitle?: string;
  readonly headline?: string;
  readonly intro?: string | null;
  readonly sections?: readonly ({ readonly title?: string; readonly body?: string } | undefined)[];
  readonly accent?: CareerAccent;
  readonly contactEmail?: string | null;
}

type SettingKey = keyof CareerSiteSettings;

/** Each setting by the name the page calls it, in the order the page shows them. */
export const SETTING_LABELS: readonly { readonly key: SettingKey; readonly label: string }[] = [
  { key: 'siteTitle', label: 'Site name' },
  { key: 'accent', label: 'Colour' },
  { key: 'headline', label: 'Headline' },
  { key: 'intro', label: 'Introduction' },
  { key: 'sections', label: 'Sections' },
  { key: 'contactEmail', label: 'Contact email' },
];

export function formValuesOf(view: CareerSiteSettings | CareerSiteSettingsView): CareerSiteSettings {
  return {
    siteTitle: view.siteTitle,
    headline: view.headline,
    intro: view.intro,
    sections: view.sections.map((section) => ({ title: section.title, body: section.body })),
    accent: view.accent,
    contactEmail: view.contactEmail,
  };
}

/** Typed text as the server will keep it: trimmed, and an empty optional field as none. */
function textOf(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/** One setting in a form that compares the way the saved value would. */
function comparable(values: WatchedSettings, key: SettingKey): string {
  if (key === 'sections') {
    return JSON.stringify((values.sections ?? []).map((section) => [textOf(section?.title), textOf(section?.body)]));
  }
  if (key === 'accent') return values.accent ?? '';
  // The server keeps an email in lower case, so "Careers@…" saved is not a change left over.
  if (key === 'contactEmail') return textOf(values.contactEmail).toLowerCase();
  return textOf(values[key]);
}

/** The settings whose value differs from `base`, by the page's own names. A trailing space is not a change. */
export function changedSettings(values: WatchedSettings, base: WatchedSettings): readonly string[] {
  return SETTING_LABELS.filter(({ key }) => comparable(values, key) !== comparable(base, key)).map(({ label }) => label);
}

/**
 * Someone else saved while this Admin was typing (review W3). Their version becomes the base; each setting this Admin
 * changed from the version they started on stays theirs, and every other setting reads as the newer version has it.
 */
export function rebasedSettings(mine: CareerSiteSettings, was: CareerSiteSettings, now: CareerSiteSettings): CareerSiteSettings {
  const keep = <K extends SettingKey>(key: K): CareerSiteSettings[K] => (comparable(mine, key) === comparable(was, key) ? now[key] : mine[key]);
  return {
    siteTitle: keep('siteTitle'),
    headline: keep('headline'),
    intro: keep('intro'),
    sections: keep('sections'),
    accent: keep('accent'),
    contactEmail: keep('contactEmail'),
  };
}

/** What Save sends: trimmed, an empty optional field as none. */
export function savedShapeOf(values: CareerSiteSettings): CareerSiteSettings {
  return {
    siteTitle: values.siteTitle.trim(),
    headline: values.headline.trim(),
    intro: textOf(values.intro) || null,
    sections: values.sections.map((section) => ({ title: section.title.trim(), body: section.body.trim() })),
    accent: values.accent,
    contactEmail: textOf(values.contactEmail).toLowerCase() || null,
  };
}

function isAccent(value: unknown): value is CareerAccent {
  return (CAREER_ACCENTS as readonly unknown[]).includes(value);
}

/** The draft the preview draws, from whatever the form holds right now. */
export function previewDraftOf(values: WatchedSettings): CareerSitePreviewInput {
  return {
    siteTitle: textOf(values.siteTitle),
    headline: textOf(values.headline),
    intro: textOf(values.intro) || null,
    sections: (values.sections ?? []).flatMap((section) => (section ? [{ title: textOf(section.title), body: textOf(section.body) }] : [])),
    accent: isAccent(values.accent) ? values.accent : 'blue',
    contactEmail: textOf(values.contactEmail) || null,
  };
}

/** "careers.nestgroup.com.bd" from the site's full address, for the preview's address bar. */
export function hostOf(siteUrl: string): string {
  try {
    const url = new URL(siteUrl);
    return `${url.host}${url.pathname === '/' ? '' : url.pathname}`;
  } catch {
    return siteUrl;
  }
}
